import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { v3Api } from '../hooks/useApi';
import { formatPct } from '../Shared/Formatters';

/**
 * Year-over-year comparison chart for a seasonal event.
 *
 * Props:
 *   - eventKey   (required): event_key slug
 *   - propertyId (optional)
 *   - metric     (optional): 'net_sales' | 'gross_sales' | 'transactions' | 'atv' (default 'net_sales')
 */
export default function EventComparisonChart({ eventKey, propertyId, metric = 'net_sales' }) {
    const { t } = useTranslation();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!eventKey) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        v3Api.seasonal.compare({ event_key: eventKey, property_id: propertyId, metric })
            .then(res => { if (!cancelled) setPayload(res?.data || null); })
            .catch(err => { if (!cancelled) setError(err?.response?.data?.error || err?.message || t('Failed to load comparison')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [eventKey, propertyId, metric, t]);

    const series = payload?.series || [];

    const { categories, values, growthLabels, growthColors } = useMemo(() => {
        const cats = series.map(s => String(s.year));
        const vals = series.map(s => Number(s.totalSales || 0));
        const labels = series.map(s => {
            if (s.growthPctVsPrev === null || s.growthPctVsPrev === undefined) return '—';
            return formatPct(s.growthPctVsPrev, 1);
        });
        const colors = series.map(s => {
            if (s.growthPctVsPrev === null || s.growthPctVsPrev === undefined) return 'var(--v3-text-muted, #9CA3AF)';
            return s.growthPctVsPrev >= 0 ? '#10B981' : '#EF4444';
        });
        return { categories: cats, values: vals, growthLabels: labels, growthColors: colors };
    }, [series]);

    const options = useMemo(() => ({
        chart: { type: 'bar', fontFamily: 'Inter, system-ui', toolbar: { show: false } },
        plotOptions: {
            bar: {
                columnWidth: '55%',
                borderRadius: 4,
                dataLabels: { position: 'top' },
            },
        },
        colors: ['#3B82F6'],
        dataLabels: {
            enabled: true,
            offsetY: -22,
            formatter: (_val, opts) => growthLabels[opts.dataPointIndex] ?? '—',
            style: {
                fontSize: '11px',
                fontFamily: 'JetBrains Mono, monospace',
                colors: growthColors,
            },
        },
        xaxis: {
            categories,
            labels: { style: { colors: 'var(--v3-text-secondary)', fontSize: '11px' } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: { colors: 'var(--v3-text-secondary)', fontSize: '11px' },
                formatter: (v) => {
                    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
                    if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
                    return v.toFixed(0);
                },
            },
        },
        grid: { borderColor: 'var(--v3-border)', strokeDashArray: 4 },
        legend: { show: false },
        tooltip: {
            theme: document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark',
            y: { formatter: (v) => Number(v).toLocaleString() },
        },
        title: {
            text: payload ? `${payload.event_name} — ${metric}` : '',
            style: { fontSize: '13px', fontWeight: 500, color: 'var(--v3-text-primary)' },
        },
    }), [categories, growthLabels, growthColors, payload, metric]);

    if (!eventKey) {
        return <div className="v3-body" style={{ padding: 20 }}>{t('Select an event to compare.')}</div>;
    }
    if (loading) {
        return <div className="v3-card" style={{ padding: 20 }}>{t('Loading comparison…')}</div>;
    }
    if (error) {
        return <div className="v3-card" style={{ padding: 20, color: 'var(--v3-text-danger, #ef4444)' }}>{t('Error')}: {error}</div>;
    }
    if (!series.length) {
        return <div className="v3-card" style={{ padding: 20 }}>{t('No occurrences yet for this event.')}</div>;
    }

    return (
        <div style={{ height: '100%', minHeight: 320 }}>
            <Chart type="bar" options={options} series={[{ name: metric, data: values }]} height="100%" />
        </div>
    );
}
